import { RepeatClockIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import { getDateTimeString, MiscarriedHistoryItem, PopulatedAssessment } from '@hb/shared'
import { useCallback, useState } from 'react'
import { updateAssessmentMiscarriedStatus } from '../../../backend/functions/other'
import { ActionLog } from '../../ActionLog'
import { UserBadge } from '../UserBadge'

const MiscarriedBadge = () => (
  <Text
    lineHeight={1.4}
    px={1.5}
    mx={1}
    borderRadius={3}
    fontWeight={600}
    fontSize="sm"
    bg="red.500"
    color="white">
    MISCARRIED
  </Text>
)

const MiscarriedUpdatedBy = ({ assessment }: { assessment: PopulatedAssessment | null }) => {
  const { miscarriedUpdatedBy, miscarriedUpdatedByGroup, miscarriedUpdatedOn } = assessment ?? {}
  return (
    <Flex borderTop="1px solid #cdcdcd" align="center" p={2} w="100%">
      <ActionLog
        action="Updated"
        on={miscarriedUpdatedOn}
        by={miscarriedUpdatedBy}
        group={miscarriedUpdatedByGroup}
      />
    </Flex>
  )
}

const MiscarriedHistoryItemView = ({ item }: { item: MiscarriedHistoryItem }) => {
  const { miscarried, on, by, group } = item
  return (
    <Flex align="center" p={2} w="100%">
      <Text
        position="relative"
        fontSize="xs"
        fontWeight={500}
        color={miscarried ? 'red.600' : 'gray.600'}
        fontFamily="Hero-New">
        {miscarried ? 'MARKED MISCARRIED' : 'UNMARKED'}
      </Text>
      <Flex ml="auto" align="center" gap={1}>
        <Text fontSize="xs" fontWeight={600} color="gray.600">
          {getDateTimeString(on, 'short')}
        </Text>
        <UserBadge userId={by} userGroup={group} />
      </Flex>
    </Flex>
  )
}

const MiscarriedHistoryView = ({ assessment }: { assessment: PopulatedAssessment | null }) => {
  const { miscarriedHistory } = assessment ?? {}

  if (!miscarriedHistory?.length) return <MiscarriedUpdatedBy assessment={assessment} />
  return (
    <VStack
      maxH="300px"
      overflowY="auto"
      borderTop="1px solid #cdcdcd"
      divider={<StackDivider />}
      w="100%"
      spacing={0}>
      {miscarriedHistory ? (
        miscarriedHistory.toReversed().map(h => <MiscarriedHistoryItemView key={h.on} item={h} />)
      ) : (
        <Text fontSize="xs" color="gray.500" fontWeight="bold">
          No miscarried history
        </Text>
      )}
    </VStack>
  )
}

export const MiscarriedHistoryPopover = ({
  assessment,
}: {
  assessment: PopulatedAssessment | null
}) => (
  <Popover trigger="hover">
    <PopoverTrigger>
      <IconButton
        ml="auto"
        aria-label="History"
        gap={1}
        bg="transparent"
        color="red.700"
        size="sm"
        icon={<RepeatClockIcon w={4} h={4} />}
        borderRadius="full"
      />
    </PopoverTrigger>
    <PopoverContent>
      <PopoverArrow />
      <PopoverBody p={0}>
        <MiscarriedHistoryView assessment={assessment} />
      </PopoverBody>
    </PopoverContent>
  </Popover>
)

const MiscarriedContent = ({
  assessment,
  assessmentId,
  canUpdate,
}: {
  assessment: PopulatedAssessment | null
  assessmentId: string | null
  canUpdate?: boolean
}) => {
  const { miscarried } = assessment ?? {}
  const [isUpdating, setIsUpdating] = useState(false)
  const { showError } = usePopUpMessage()
  const handleUpdateMiscarriedStatus = useCallback(async () => {
    if (!assessmentId) {
      showError('Error updating miscarried status', 'No assessment ID')
      return
    }
    try {
      setIsUpdating(true)
      await updateAssessmentMiscarriedStatus({
        assessmentId,
        miscarried: !miscarried,
      })
    } catch (e: any) {
      console.error('Error updating miscarried status:', e.message)
      showError('Error updating miscarried status', e.message)
    } finally {
      setIsUpdating(false)
    }
  }, [assessmentId, miscarried, showError])
  return (
    <VStack w="100%" spacing={0}>
      {canUpdate ? (
        <Flex p={2} w="100%" bg="gray.100">
          <Button
            w="100%"
            bg="white"
            isLoading={isUpdating}
            onClick={handleUpdateMiscarriedStatus}
            size="xs"
            colorScheme={miscarried ? 'red' : 'gray'}
            variant="outline">
            {miscarried ? 'Mark as Not Miscarried' : 'Mark as Miscarried'}
          </Button>
        </Flex>
      ) : null}
      {miscarried ? <MiscarriedHistoryView assessment={assessment} /> : null}
    </VStack>
  )
}

export const MiscarriedPopover = ({
  assessment,
  assessmentId,
  placement = 'bottom',
  canUpdate,
}: {
  assessment: PopulatedAssessment | null
  assessmentId: string | null
  placement?: PopoverProps['placement']
  canUpdate?: boolean
}) => (
  <Popover trigger="hover" closeOnBlur={false} strategy="fixed" isLazy placement={placement}>
    <PopoverTrigger>
      <HStack>
        <Button variant="unstyled">
          <MiscarriedBadge />
        </Button>
      </HStack>
    </PopoverTrigger>
    <Portal>
      <PopoverContent w="350px">
        <PopoverArrow bg={placement === 'bottom' ? 'red.600' : 'gray.50'} />
        <PopoverBody borderRadius={6} overflow="hidden" p={0}>
          <HStack bg="red.600" borderBottom="1px solid #cdcdcd" px={2} py={1}>
            <Text color="red.50" fontWeight={600}>
              MISCARRIED STATUS
            </Text>
            {/* <PopoverCloseButton color="gray.50" /> */}
          </HStack>
          <VStack bg="gray.50" spacing={0}>
            <MiscarriedContent
              assessment={assessment}
              assessmentId={assessmentId}
              canUpdate={canUpdate}
            />
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
)
